import { Camera } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

export type CameraStreamErrorCode =
  | 'unsupported'
  | 'insecureContext'
  | 'permissionDenied'
  | 'notFound';

export class CameraStreamError extends Error {
  readonly code: CameraStreamErrorCode;

  constructor(code: CameraStreamErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'CameraStreamError';
    this.code = code;
  }
}

type LegacyNavigator = Navigator & {
  getUserMedia?: (
    constraints: MediaStreamConstraints,
    success: (stream: MediaStream) => void,
    error: (err: unknown) => void,
  ) => void;
  webkitGetUserMedia?: LegacyNavigator['getUserMedia'];
  mozGetUserMedia?: LegacyNavigator['getUserMedia'];
};

function isCapacitorBundledOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  const protocol = window.location.protocol;
  return (
    protocol === 'capacitor:' || protocol === 'ionic:' || protocol === 'file:'
  );
}

/** Polyfill navigator.mediaDevices.getUserMedia for older WebViews (incl. some Capacitor iOS). */
export function ensureMediaDevicesPolyfill(): boolean {
  if (typeof navigator === 'undefined') return false;

  if (navigator.mediaDevices?.getUserMedia != null) {
    return true;
  }

  const nav = navigator as LegacyNavigator;
  const legacyGetUserMedia =
    nav.getUserMedia?.bind(nav) ??
    nav.webkitGetUserMedia?.bind(nav) ??
    nav.mozGetUserMedia?.bind(nav);

  if (!legacyGetUserMedia) {
    return false;
  }

  if (!navigator.mediaDevices) {
    (navigator as Navigator & { mediaDevices: MediaDevices }).mediaDevices =
      {} as MediaDevices;
  }

  navigator.mediaDevices.getUserMedia = (constraints) =>
    new Promise((resolve, reject) => {
      legacyGetUserMedia(constraints ?? { video: true }, resolve, reject);
    });

  return true;
}

async function ensureNativeCameraPermission(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const current = await Camera.checkPermissions();
  if (current.camera === 'denied') {
    throw new CameraStreamError('permissionDenied');
  }
  if (current.camera !== 'granted') {
    const requested = await Camera.requestPermissions({
      permissions: ['camera'],
    });
    if (requested.camera !== 'granted') {
      throw new CameraStreamError('permissionDenied');
    }
  }
}

const PREFERRED_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: { ideal: 'environment' },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};

const FALLBACK_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: { ideal: 'environment' },
};

const PRODUCT_SCAN_CAMERA_STORAGE_KEY = 'up2promo-product-scan-camera-id';

export type VideoInputDevice = {
  deviceId: string;
  label: string;
};

function formatVideoInputLabel(device: MediaDeviceInfo, index: number): string {
  const label = device.label?.trim();
  if (label) return label;
  return `Camera ${index + 1}`;
}

export function getStoredVideoInputDeviceId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(PRODUCT_SCAN_CAMERA_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeVideoInputDeviceId(deviceId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(PRODUCT_SCAN_CAMERA_STORAGE_KEY, deviceId);
  } catch {
    // ignore quota / private mode
  }
}

/** Lists available video inputs after camera permission is granted. */
export async function listVideoInputDevices(): Promise<VideoInputDevice[]> {
  const stream = await getCameraMediaStream();
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === 'videoinput' && device.deviceId)
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: formatVideoInputLabel(device, index),
      }));
  } finally {
    for (const track of stream.getTracks()) {
      track.stop();
    }
  }
}

export function pickPreferredVideoInputDevice(
  devices: VideoInputDevice[],
  preferredDeviceId?: string | null,
): string | null {
  if (devices.length === 0) return null;
  if (
    preferredDeviceId &&
    devices.some((device) => device.deviceId === preferredDeviceId)
  ) {
    return preferredDeviceId;
  }

  const externalPattern = /external|usb|hd|webcam|logitech|elgato|obs/i;
  const external = devices.find((device) => externalPattern.test(device.label));
  if (external) return external.deviceId;

  const backPattern = /back|rear|environment/i;
  const back = devices.find((device) => backPattern.test(device.label));
  if (back) return back.deviceId;

  const frontPattern = /front|facetime|user|selfie|integrated/i;
  const nonFront = devices.find((device) => !frontPattern.test(device.label));
  if (nonFront) return nonFront.deviceId;

  return devices[0]?.deviceId ?? null;
}

/** Camera id or constraints for html5-qrcode after permission + polyfill are ready. */
export async function resolveQrScannerCameraConfig(
  deviceId?: string,
): Promise<string | MediaTrackConstraints> {
  if (deviceId) {
    return deviceId;
  }

  const stream = await getCameraMediaStream();
  try {
    const resolvedDeviceId = stream.getVideoTracks()[0]?.getSettings().deviceId;
    if (resolvedDeviceId) {
      return resolvedDeviceId;
    }
  } finally {
    for (const track of stream.getTracks()) {
      track.stop();
    }
  }

  return { facingMode: 'environment' };
}

export async function getCameraMediaStream(
  deviceId?: string,
): Promise<MediaStream> {
  if (!ensureMediaDevicesPolyfill()) {
    if (
      typeof window !== 'undefined' &&
      !window.isSecureContext &&
      !isCapacitorBundledOrigin()
    ) {
      throw new CameraStreamError('insecureContext');
    }
    throw new CameraStreamError('unsupported');
  }

  await ensureNativeCameraPermission();

  const videoConstraints: MediaTrackConstraints = deviceId
    ? {
        deviceId: { exact: deviceId },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      }
    : PREFERRED_VIDEO_CONSTRAINTS;

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: videoConstraints,
    });
  } catch (err) {
    if (err instanceof CameraStreamError) {
      throw err;
    }
    if (
      deviceId &&
      err instanceof DOMException &&
      (err.name === 'OverconstrainedError' ||
        err.name === 'ConstraintNotSatisfiedError' ||
        err.name === 'NotFoundError')
    ) {
      return navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { deviceId: { exact: deviceId } },
      });
    }
    if (
      err instanceof DOMException &&
      (err.name === 'OverconstrainedError' ||
        err.name === 'ConstraintNotSatisfiedError')
    ) {
      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: FALLBACK_VIDEO_CONSTRAINTS,
        });
      } catch (retryErr) {
        throw toCameraStreamError(retryErr);
      }
    }
    throw toCameraStreamError(err);
  }
}

/** Maps browser `getUserMedia` failures to codes the UI has copy for. */
function toCameraStreamError(err: unknown): unknown {
  if (!(err instanceof DOMException)) return err;
  if (err.name === 'NotAllowedError') {
    return new CameraStreamError('permissionDenied');
  }
  if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
    return new CameraStreamError('notFound');
  }
  return err;
}

/**
 * User-facing copy for a camera start failure. Never returns the browser's own
 * (English, technical) error message: unknown failures get `labels.default`.
 */
export function getCameraStreamErrorMessage(
  err: unknown,
  labels: {
    default: string;
    insecureContext: string;
    permissionDenied: string;
    notFound?: string;
  },
): string {
  const mapped = toCameraStreamError(err);
  if (mapped instanceof CameraStreamError) {
    switch (mapped.code) {
      case 'insecureContext':
        return labels.insecureContext;
      case 'permissionDenied':
        return labels.permissionDenied;
      case 'notFound':
        return labels.notFound ?? labels.default;
      default:
        return labels.default;
    }
  }
  return labels.default;
}
